import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { useSession } from '../session/SessionProvider.js'
import '../styles/forms.css'
import './settings.css'

/**
 * I consumi su cui l'associazione viene fatturata.
 *
 * **Non c'è un importo, ed è una scelta.** Il listino lo tiene il team che
 * emette le fatture, e il loro modello guarda il massimo raggiunto nel
 * periodo, non il valore di oggi. Un totale calcolato qui sarebbe una cifra
 * plausibile e sbagliata — cioè peggio di nessuna cifra, perché verrebbe
 * creduta e poi contestata a fattura arrivata.
 *
 * Quello che si può dare, e che serve davvero, è **lo stesso numero che loro
 * useranno**, letto dalla stessa fonte e con la stessa definizione di
 * «attivo». La ripartizione per specie c'è perché un totale che non torna si
 * controlla una specie alla volta; un totale e basta si può solo accettare.
 *
 * La data è modificabile perché la domanda vera in segreteria non è «quanti
 * sono adesso» ma «quanti erano a fine mese», quando arriva la fattura.
 */
export function BillingUsage() {
  const { resources, context } = useSession()
  const [quando, setQuando] = useState('')

  const consumi = useQuery({
    queryKey: ['billing-usage', quando],
    queryFn: ({ signal }) => resources.billing.usage(quando || undefined, signal),
  })

  const nomeSpecie = (codice: string) =>
    context?.species.find((s) => s.code === codice)?.name ?? codice

  return (
    <section aria-labelledby="titolo-consumi">
      <div className="elenco__testa">
        <h1 id="titolo-consumi">Consumi</h1>
      </div>

      <label className="elenco__ricerca">
        <span>Situazione alla data</span>
        <input
          type="date"
          value={quando}
          onChange={(evento) => {
            setQuando(evento.target.value)
          }}
        />
      </label>

      {consumi.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere i consumi.
        </p>
      )}

      {consumi.isPending && <p className="messaggio">Caricamento…</p>}

      {consumi.data && (
        <>
          <p className="consumi__totale">
            <strong>{consumi.data.active.toLocaleString('it-IT')}</strong> soggetti attivi al{' '}
            {data(consumi.data.on)}
          </p>

          <div className="tabella-scorrevole">
            <table>
              <thead>
                <tr>
                  <th scope="col">Specie</th>
                  <th scope="col">Attivi</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(consumi.data.by_species).map(([codice, quanti]) => (
                  <tr key={codice}>
                    <td>{nomeSpecie(codice)}</td>
                    <td className="consumi__numero">{quanti.toLocaleString('it-IT')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="messaggio">
            È lo stesso conteggio che legge chi emette la fattura, con la stessa definizione di
            soggetto attivo. L'importo non compare qui: il listino non è di questo sistema, e la
            fatturazione guarda il massimo raggiunto nel periodo, non il valore di oggi — una cifra
            calcolata qui rischierebbe di non coincidere.
          </p>
        </>
      )}
    </section>
  )
}

function data(iso: string): string {
  const quando = new Date(iso)

  return Number.isNaN(quando.getTime()) ? iso : quando.toLocaleDateString('it-IT')
}
